import { useLocale } from './context'

export type DateLike = Date | number | string

export function useFormatDate() {
  const locale = useLocale()
  const formatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
  })

  return (date: DateLike) => formatter.format(toDate(date))
}

export function useFormatDateRange() {
  const locale = useLocale()
  const formatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
  })

  return (start: DateLike, end: DateLike) => {
    const startDate = toDate(start)
    const endDate = toDate(end)
    return formatter.formatRange(startDate, endDate)
  }
}

const MS_PER_DAY = 1000 * 60 * 60 * 24

export function useFormatCompactDateTime() {
  const locale = useLocale()
  const timeFormatter = new Intl.DateTimeFormat(locale, { timeStyle: 'short' })
  const relativeFormatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const shortDateFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'narrow' })
  const longDateFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'narrow', year: 'numeric' })

  return (timestamp: DateLike) => {
    const date = toDate(timestamp)
    const now = new Date()
    const diffInDays = dateDiff(date, now)
    if (Math.abs(diffInDays) <= 1) {
      const relativeDate = relativeFormatter.format(diffInDays, 'day')
      return `${relativeDate} ${timeFormatter.format(date)}`
    }
    if (date.getFullYear() === now.getFullYear()) {
      return shortDateFormatter.format(date)
    }
    return longDateFormatter.format(date)
  }
}

export function dateDiff(date1: Date, date2: Date) {
  const justDate = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffInMs = justDate(date1).getTime() - justDate(date2).getTime()
  return Math.round(diffInMs / MS_PER_DAY)
}

export function useFormatDateTime() {
  const locale = useLocale()
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  })

  return (date: DateLike) => formatter.format(toDate(date))
}

export function useFormatTime() {
  const locale = useLocale()
  const formatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: undefined,
  })

  return (date: DateLike) => `klo ${formatter.format(toDate(date))}`
}

const toDate = (date: DateLike) => date instanceof Date ? date : new Date(date)

export function useFormatDuration() {
  const locale = useLocale()
  const formatter = new Intl.DurationFormat(locale, { style: 'long' })

  return (seconds: number) => formatter.format(toAproximateDuration(seconds))
}

const timeUnits = [
  { name: 'seconds', max: 60 },
  { name: 'minutes', max: 60 },
  { name: 'hours', max: 24 },
  { name: 'days', max: Infinity },
] as const

type DurationUnit = (typeof timeUnits)[number]['name']

function toAproximateDuration(seconds: number): Intl.DurationInput {
  const unitValues: [DurationUnit, number][] = []
  let value = Math.round(seconds)

  for (const unit of timeUnits) {
    const unitValue = value % unit.max
    unitValues.push([unit.name, unitValue])

    value = (value - unitValue) / unit.max

    if (value == 0) break
  }
  // If some units are not shown and the value of the last unshown unit is
  // over the unit's halfpoint, add one to the next unit to implement proper rounding
  if (unitValues.length > 2) {
    const roundIndex = unitValues.length - 3
    const max = timeUnits[roundIndex].max
    const roundValue = unitValues[roundIndex][1]
    if ((roundValue << 1) >= max) {
      unitValues[roundIndex + 1][1] += 1
    }
  }

  return Object.fromEntries(unitValues.slice(-2))
}
